import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  MorphemeInfoPanelAccordionScrollControlContext,
  type MorphemeInfoPanelAccordionScrollView,
} from "#morpheme-info-panel-accordion-scroll-control";
import type { ReactNode } from "react";
import { useCallback, useContext, useRef } from "react";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";

export function MorphemeInfoPanelAccordionStickyHeader({
  iconName,
  isLoading,
  isOpen,
  onToggle,
  title,
  trailing: trailingProp,
}: {
  iconName: IconName;
  // Swap the collapse/expand chevron for a small spinner and make the header inert — the standard
  // loading affordance for a section, matching `SectionAccordionNodes`' `isLoading`. Ignored when
  // `trailing` is set.
  isLoading?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  title: string;
  // When set (e.g. a loading spinner), the right slot renders this node instead of the
  // collapse/expand chevron and the header is inert — matches the deck/stats index loading shells
  // (`SectionAccordionStickyHeader`'s `trailing` prop).
  trailing?: ReactNode;
}): ReactNode {
  const trailing = trailingProp ?? (isLoading ? <ActivityIndicator size="small" /> : undefined);
  const scrollControl = useContext(MorphemeInfoPanelAccordionScrollControlContext);
  const headerShellRef = useRef<View>(null);

  const handleChevronPress = useCallback(() => {
    onToggle();
  }, [onToggle]);

  const handleTitlePress = useCallback(() => {
    const scrollY = scrollControl.scrollYRef.current;
    if (scrollY <= 0) {
      onToggle();
      return;
    }

    const scroll: MorphemeInfoPanelAccordionScrollView | null = scrollControl.scrollRef.current;
    const header = headerShellRef.current;
    if (scroll == null || header == null) {
      onToggle();
      return;
    }

    const innerView = scroll.getInnerViewRef?.();
    if (innerView == null) {
      onToggle();
      return;
    }

    header.measureLayout(
      innerView,
      (_x, naturalY) => {
        if (scrollY > naturalY) {
          scrollControl.scrollHeaderIntoView(headerShellRef);
        } else {
          onToggle();
        }
      },
      () => {
        onToggle();
      },
    );
  }, [scrollControl, onToggle]);

  return (
    <View
      className="border-b border-border bg-surface-header"
      collapsable={false}
      ref={headerShellRef}
    >
      <View className="mx-2.25">
        <View className="flex-row items-center justify-between">
          <Pressable
            accessibilityLabel={`${title} section`}
            accessibilityRole="button"
            accessibilityState={{ expanded: isOpen }}
            className="min-h-0 flex-1 flex-row items-center gap-2 py-2 active:bg-accent"
            disabled={trailing !== undefined}
            onPress={handleTitlePress}
          >
            <Icon className="text-muted-foreground" name={iconName} size={16} />
            <Text className="text-base leading-5 text-foreground">{title}</Text>
          </Pressable>
          {trailing === undefined ? (
            <Pressable
              accessibilityLabel={isOpen ? `Collapse ${title}` : `Expand ${title}`}
              accessibilityRole="button"
              accessibilityState={{ expanded: isOpen }}
              className="py-2 pl-2 active:bg-accent"
              hitSlop={8}
              onPress={handleChevronPress}
            >
              <Icon
                className="text-foreground"
                name={isOpen ? "LuChevronUp" : "LuChevronDown"}
                size={18}
              />
            </Pressable>
          ) : (
            <View className="py-2 pl-2">{trailing}</View>
          )}
        </View>
      </View>
    </View>
  );
}
